<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteDraft } from '@/composables/useSiteDraft'
import { useSiteGiving } from '@/composables/useSiteGiving'
import Spinner from '@/components/ui/Spinner.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import SiteImageField from '@/components/Sites/SiteImageField.vue'
import BroadcastServiceInfoCard from '@/components/Sites/Broadcast/BroadcastServiceInfoCard.vue'
import BroadcastChatCard from '@/components/Sites/Broadcast/BroadcastChatCard.vue'
import BroadcastGivingCard from '@/components/Sites/Broadcast/BroadcastGivingCard.vue'
import BroadcastEventExtrasCard from '@/components/Sites/Broadcast/BroadcastEventExtrasCard.vue'
import { normalizeAudience, audienceLabels, linkUrlError, backupUrlError, LINKS_MAX, LINK_LABEL_MAX, BROADCAST_TEXT_FIELDS, defaultText } from '@/components/Sites/broadcastAudience'
import { isBroadcast as isBroadcastSite, browserTimeZone, canonicalTimeZone, SERVICE_DURATION_MIN, SERVICE_DURATION_MAX, SERVICE_TIMES_MAX } from '@/components/Sites/siteTemplates'

// Settings › Broadcast (sites-broadcast-template-contract.md §7): the three
// cards of the broadcast template. Service info is a runtime setting saved
// like Theme — only the keys that differ are PATCHed, to the draft
// (`?target=draft`), and the shell's draft / publish bar takes over. Giving
// saves itself through castr's `PUT /sites/{id}/giving` (settings + paywall
// donation product in one call). Chat is informational: the site makes room
// for the player's chat automatically (runtime v0.18).
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const draft = useSiteDraft(() => props.siteId)
const giving = useSiteGiving(() => props.siteId)

const isBroadcast = computed(() => isBroadcastSite(props.site))
// Settings › Audience owns the preset; this page only follows its wording.
const audience = computed(() => normalizeAudience(settings.value.broadcastAudience))
const labels = computed(() => audienceLabels(audience.value))
const sponsorLabel = computed(() => {
	const field = BROADCAST_TEXT_FIELDS.find((f) => f.key === 'sponsorLabel')
	return settings.value.broadcastTexts?.sponsorLabel || defaultText(field, audience.value)
})

const loading = ref(true)
const loadError = ref(null)
const saving = ref(false)
// `settings` = published ⊕ draft (what the preview shows).
const settings = ref({})
const writableKeys = ref(null)
const liveItems = ref([])
const categories = ref([])
const monetization = ref(null)
const plans = ref([])

const KEYS = ['broadcastTagline', 'broadcastTimezone', 'broadcastServiceTimes', 'broadcastAbout', 'broadcastAddress', 'broadcastGivingMessage', 'broadcastLiveItemId', 'broadcastRecentServicesEnabled', 'broadcastRecentServicesCategoryId', 'broadcastCoverImage', 'broadcastSponsorLogo', 'broadcastLinks', 'broadcastBackupStreamUrl']
const EXTRAS_KEYS = ['broadcastSponsorLogo', 'broadcastLinks', 'broadcastBackupStreamUrl']
// Nothing saved yet → the owner's own zone is the likely answer (Trello
// 1GamnIFQ); it is written with the first service time, see `patch`.
const DEFAULTS = {
	broadcastTagline: '',
	broadcastTimezone: browserTimeZone() || 'UTC',
	broadcastServiceTimes: [],
	broadcastAbout: '',
	broadcastAddress: '',
	broadcastGivingMessage: '',
	broadcastLiveItemId: 0,
	broadcastRecentServicesEnabled: true,
	broadcastRecentServicesCategoryId: 0,
	broadcastCoverImage: '',
	broadcastSponsorLogo: '',
	broadcastLinks: [],
	broadcastBackupStreamUrl: '',
}
const normLinks = (list) => (Array.isArray(list) ? list : []).map((l) => ({ label: String(l?.label ?? ''), url: String(l?.url ?? '') }))
const normRows = (rows) => (Array.isArray(rows) ? rows : []).map((r) => ({
	label: String(r?.label ?? ''),
	weekday: Number.isInteger(Number(r?.weekday)) ? Number(r.weekday) : 0,
	time: String(r?.time ?? ''),
	durationMinutes: r?.durationMinutes === undefined || r?.durationMinutes === null || r?.durationMinutes === '' ? null : Number(r.durationMinutes),
}))
/** Runtime values → form values (defaults for unset keys). */
const fromSettings = (s) => ({
	broadcastTagline: s.broadcastTagline || '',
	broadcastTimezone: canonicalTimeZone(s.broadcastTimezone) || DEFAULTS.broadcastTimezone,
	broadcastServiceTimes: normRows(s.broadcastServiceTimes),
	broadcastAbout: s.broadcastAbout || '',
	broadcastAddress: s.broadcastAddress || '',
	broadcastGivingMessage: s.broadcastGivingMessage || '',
	broadcastLiveItemId: Number(s.broadcastLiveItemId) || 0,
	broadcastRecentServicesEnabled: s.broadcastRecentServicesEnabled !== false,
	broadcastRecentServicesCategoryId: Number(s.broadcastRecentServicesCategoryId) || 0,
	broadcastCoverImage: s.broadcastCoverImage || '',
	broadcastSponsorLogo: s.broadcastSponsorLogo || '',
	broadcastLinks: normLinks(s.broadcastLinks),
	broadcastBackupStreamUrl: s.broadcastBackupStreamUrl || '',
})
const form = ref(fromSettings({}))
const base = ref(fromSettings({}))
const fillForm = () => {
	base.value = fromSettings(settings.value)
	form.value = JSON.parse(JSON.stringify(base.value))
}

const writable = (key) => !Array.isArray(writableKeys.value) || writableKeys.value.includes(key)
// A runtime that predates v0.15 does not list the broadcast keys: nothing can be saved.
const supported = computed(() => !Array.isArray(writableKeys.value) || writableKeys.value.includes('broadcastTagline'))

const load = async () => {
	try {
		const [res, liveRes, categoryRes, monetizationRes, plansRes] = await Promise.all([
			SiteAdminService.getSettings(props.siteId),
			SiteAdminService.listContent(props.siteId, { type: 'live' }).catch(() => ({ items: [] })),
			SiteAdminService.listCategories(props.siteId).catch(() => ({ items: [] })),
			SiteAdminService.getMonetization(props.siteId).catch(() => null),
			SiteAdminService.listPlans(props.siteId).catch(() => null),
		])
		settings.value = { ...(res?.settings || {}), ...(res?.draft && typeof res.draft === 'object' ? res.draft : {}) }
		writableKeys.value = Array.isArray(res?.writableKeys) ? res.writableKeys : null
		// `type=live` is filtered server-side; filter again for an older runtime that ignores it.
		liveItems.value = (liveRes?.items || []).filter((i) => i.type === 'live')
		categories.value = categoryRes?.items || []
		monetization.value = monetizationRes
		plans.value = plansRes?.products || []
		draft.applySettingsResponse(res)
		if (typeof res?.draftCount !== 'number' && !res?.draft) draft.state.value.draftCount = null
		fillForm()
		loadError.value = null
	} catch (err) {
		loadError.value = err
	}
}
onMounted(async () => { loading.value = true; await Promise.all([load(), giving.load()]); loading.value = false })

const liveItem = computed(() => {
	const id = Number(form.value.broadcastLiveItemId)
	if (id) return liveItems.value.find((i) => Number(i.id) === id) || null
	return liveItems.value.find((i) => i.published !== false) || liveItems.value[0] || null
})
const stripeConnected = computed(() => (monetization.value?.stripe ? monetization.value.stripe.connected === true : null))
const defaultCurrency = computed(() => plans.value.find((p) => p.currency)?.currency?.toUpperCase() || 'USD')

// --- validation (mirrors the runtime rules, contract §2.2) -------------------------
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
const errors = computed(() => {
	const e = {}
	const f = form.value
	if (String(f.broadcastTagline || '').length > 140) e.broadcastTagline = 'Up to 140 characters'
	if (String(f.broadcastAddress || '').length > 300) e.broadcastAddress = 'Up to 300 characters'
	if (String(f.broadcastGivingMessage || '').length > 500) e.broadcastGivingMessage = 'Up to 500 characters'
	if (String(f.broadcastAbout || '').length > 20000) e.broadcastAbout = 'Up to 20 000 characters'
	const rows = f.broadcastServiceTimes
	if (rows.length > SERVICE_TIMES_MAX) e.broadcastServiceTimes = `Up to ${SERVICE_TIMES_MAX} service times`
	const seen = new Set()
	e.serviceTimes = rows.map((r) => {
		const label = String(r.label || '').trim()
		if (!label) return 'Add a label, e.g. “Sunday Service”'
		if (label.length > 40) return 'Label: up to 40 characters'
		if (!TIME.test(String(r.time || ''))) return 'Pick a start time'
		if (r.durationMinutes !== null && (!Number.isInteger(r.durationMinutes) || r.durationMinutes < SERVICE_DURATION_MIN || r.durationMinutes > SERVICE_DURATION_MAX)) return `Duration: ${SERVICE_DURATION_MIN}–${SERVICE_DURATION_MAX} minutes, or empty`
		const key = `${r.weekday}-${r.time}`
		if (seen.has(key)) return 'Another service already starts at this day and time'
		seen.add(key)
		return null
	})
	if (f.broadcastLinks.length > LINKS_MAX) e.broadcastLinks = `Up to ${LINKS_MAX} links`
	e.links = f.broadcastLinks.map((l) => {
		const label = String(l.label || '').trim()
		if (!label) return 'Add a label for this link'
		if (label.length > LINK_LABEL_MAX) return `Label: up to ${LINK_LABEL_MAX} characters`
		return linkUrlError(l.url)
	})
	const backup = backupUrlError(f.broadcastBackupStreamUrl)
	if (backup) e.broadcastBackupStreamUrl = backup
	return e
})
const valid = computed(() => Object.entries(errors.value).every(([k, v]) => (k === 'serviceTimes' || k === 'links' ? !v.some(Boolean) : !v)))

// --- changes: only keys that differ from the loaded values -------------------------
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)
const toRuntime = (key, value) => {
	if (key === 'broadcastServiceTimes') {
		return value.map((r) => ({ label: String(r.label).trim(), weekday: r.weekday, time: r.time, ...(r.durationMinutes !== null ? { durationMinutes: r.durationMinutes } : {}) }))
	}
	if (key === 'broadcastLiveItemId') return Number(value) || null
	if (key === 'broadcastLinks') return value.map((l) => ({ label: String(l.label).trim(), url: String(l.url).trim() }))
	if (key === 'broadcastTimezone') return canonicalTimeZone(value)
	if (['broadcastTagline', 'broadcastAbout', 'broadcastAddress', 'broadcastGivingMessage', 'broadcastCoverImage', 'broadcastSponsorLogo', 'broadcastBackupStreamUrl'].includes(key)) {
		// '' clears the row (back to default) on the runtime.
		return String(value ?? '').trim() === '' ? null : String(value).trim()
	}
	return value
}
// The zone shown is only a browser guess until the runtime has one; it is
// persisted together with the first service times so the countdown is right.
const timezoneSuggested = computed(() => !settings.value.broadcastTimezone)
const patch = computed(() => {
	const p = {}
	for (const key of KEYS) {
		if (!writable(key)) continue
		if (!same(form.value[key], base.value[key])) p[key] = toRuntime(key, form.value[key])
	}
	if (timezoneSuggested.value && writable('broadcastTimezone') && 'broadcastServiceTimes' in p && !('broadcastTimezone' in p) && form.value.broadcastTimezone !== 'UTC') {
		p.broadcastTimezone = canonicalTimeZone(form.value.broadcastTimezone)
	}
	return p
})
const unsavedCount = computed(() => Object.keys(patch.value).length)
const coverDirty = computed(() => 'broadcastCoverImage' in patch.value)
const extrasDirty = computed(() => EXTRAS_KEYS.some((k) => k in patch.value))
const infoDirty = computed(() => Object.keys(patch.value).some((k) => k !== 'broadcastCoverImage' && !EXTRAS_KEYS.includes(k)))

// --- draft / publish state ---------------------------------------------------------
const draftSupported = computed(() => draft.draftSupported.value)
const unpublishedCount = computed(() => draft.draftCount.value)
const publishing = computed(() => draft.state.value.publishing)
const saveLabel = computed(() => (draftSupported.value ? 'Save draft' : 'Save settings'))

const save = async () => {
	if (!valid.value || saving.value || !unsavedCount.value) return
	saving.value = true
	const body = { ...patch.value }
	try {
		const res = await SiteAdminService.updateSettings(props.siteId, body, { target: 'draft' })
		// Our baseline is published ⊕ draft; the answer echoes the published values.
		for (const [key, value] of Object.entries(body)) settings.value = { ...settings.value, [key]: value }
		if (Array.isArray(res?.writableKeys)) writableKeys.value = res.writableKeys
		draft.noteDraftSaved(res)
		fillForm()
		// The giving message is read by the giving card too.
		if ('broadcastGivingMessage' in body) giving.load({ force: true })
		const n = Object.keys(body).length
		notify({ group: 'success', text: draft.draftSupported.value ? `${n} ${n === 1 ? 'change' : 'changes'} saved to the draft. Publish when you are ready.` : 'Settings saved.' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not save the broadcast settings.' })
	}
	saving.value = false
}
const publish = () => draft.publish().catch(() => null)
const discardUnsaved = () => fillForm()
const onGivingSaved = () => draft.refresh()

const beforeUnload = (e) => { if (unsavedCount.value) { e.preventDefault(); e.returnValue = '' } }
onMounted(() => window.addEventListener('beforeunload', beforeUnload))
onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))
</script>

<template>
	<div data-testid="site-broadcast">
		<div class="mb-4 flex flex-wrap items-start justify-between gap-3">
			<div class="min-w-0 flex-1">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Broadcast</h2>
				<p class="mb-0 text-tiny text-surface-7">
					{{ labels.pageDescription }}
					<template v-if="draftSupported">Changes are saved as a draft and go live when you publish.</template>
					<template v-else>Saved changes go live within a minute.</template>
				</p>
			</div>
		</div>

		<Alert v-if="!isBroadcast" type="normal" class="mb-4" data-testid="broadcast-not-active">
			This site uses the Video library template. These settings apply once you switch to Broadcast under
			<router-link :to="{ name: 'SiteGeneral', params: { siteId } }" class="underline">Settings › General</router-link>; you can prepare them now.
		</Alert>

		<Alert v-if="loadError" type="error" class="mb-4">{{ loadError.message }}</Alert>
		<Spinner v-else-if="loading" text="Loading settings…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<template v-else>
			<p v-if="!supported" class="mb-4 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-7" data-testid="broadcast-unsupported">
				Broadcast settings need a newer site runtime — they appear here as soon as your site is updated.
			</p>

			<!-- Not a <form>: the Giving card carries its own -->
			<div class="flex flex-col gap-4" data-testid="broadcast-form">
				<div class="grid grid-cols-1 gap-4 2xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] 2xl:items-start">
					<div class="flex flex-col gap-4">
						<!-- Cover image: site-admin API v0.17; hidden on an older runtime that does not list the key -->
						<section v-if="writable('broadcastCoverImage')" class="rounded-lg bg-surface-2 p-4 sm:p-5" :class="coverDirty ? 'ring-1 ring-gold/40' : ''" data-testid="broadcast-cover">
							<SiteImageField id="broadcast-cover-image" v-model="form.broadcastCoverImage" :site-id="siteId" label="Cover image"
								hint="Shown behind the countdown while you're offline. 1920×1080 recommended." />
						</section>
						<BroadcastServiceInfoCard v-model="form" :live-items="liveItems" :categories="categories" :errors="errors" :writable-keys="writableKeys" :disabled="!supported" :timezone-suggested="timezoneSuggested" :audience="audience" :class="infoDirty ? 'ring-1 ring-gold/40' : ''" />
					</div>
					<div class="flex flex-col gap-4">
						<BroadcastEventExtrasCard v-model="form" :site-id="siteId" :errors="errors" :writable-keys="writableKeys" :disabled="!supported" :sponsor-label="sponsorLabel" :class="extrasDirty ? 'ring-1 ring-gold/40' : ''" />
						<BroadcastChatCard :live-item="liveItem" />
						<BroadcastGivingCard :site-id="siteId" :stripe-connected="stripeConnected" :default-currency="defaultCurrency" @saved="onGivingSaved" />
					</div>
				</div>

				<!-- Sticky save bar for Service info + cover (the Giving card saves itself) -->
				<div class="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center gap-3 border-t border-surface-3 bg-surface-1/95 px-4 py-3 backdrop-blur lg:mx-0 lg:rounded-lg lg:border lg:px-5" data-testid="broadcast-save-bar">
					<p class="mb-0 mr-auto flex min-w-0 flex-wrap items-center gap-x-2 text-tiny" data-testid="broadcast-change-count">
						<template v-if="unsavedCount">
							<span class="size-2 shrink-0 rounded-full bg-gold" aria-hidden="true"></span>
							<span class="text-white">{{ unsavedCount }} unsaved {{ unsavedCount === 1 ? 'change' : 'changes' }}</span>
							<span class="text-surface-7">in {{ [coverDirty ? 'Cover image' : null, infoDirty ? labels.infoTitle : null, extrasDirty ? 'Sponsor & links' : null].filter(Boolean).join(', ') }}</span>
							<button type="button" class="text-surface-8 underline hover:text-white" data-testid="broadcast-discard-unsaved" @click="discardUnsaved">Discard</button>
						</template>
						<template v-else-if="draftSupported && unpublishedCount">
							<span class="size-2 shrink-0 rounded-full bg-main" aria-hidden="true"></span>
							<span class="text-white">{{ unpublishedCount }} unpublished {{ unpublishedCount === 1 ? 'change' : 'changes' }}</span>
							<span class="text-surface-7">— viewers still see the published version.</span>
						</template>
						<template v-else>
							<span class="size-2 shrink-0 rounded-full bg-success" aria-hidden="true"></span>
							<span class="text-surface-7">{{ draftSupported ? 'Everything is published.' : 'All changes saved.' }}</span>
						</template>
					</p>
					<FormButton v-if="!unsavedCount && draftSupported && unpublishedCount" type="main" size="md" class="w-full justify-center sm:w-auto" :is-loading="publishing" :disabled="publishing" data-testid="broadcast-publish" @click="publish">
						Publish changes
					</FormButton>
					<FormButton v-else type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!valid || saving || !unsavedCount || !supported" :is-loading="saving" data-testid="broadcast-save" @click="save">
						{{ saveLabel }}
					</FormButton>
				</div>
			</div>
		</template>
	</div>
</template>
